import React, { useState, useCallback, useRef } from 'react'
import AccountUsageIndicator from '../../components/AccountUsageIndicator'
import ConfirmModal from '../../components/ConfirmModal'
import MediaViewer from '../../components/MediaViewer'
import Tooltip from '../../components/Tooltip'
import { Select, Switch } from '../../components/ui'
import MediaEditModal from '../../components/MediaEditModal'
import GroupEditModal from '../../components/GroupEditModal'
import { MediaComposition } from '../../components/MediaTypeLabel'
import ModelDetailMediaItem from './ModelDetailMediaItem'
import SessionEditModal from './SessionEditModal'
import AudioLibraryPanel from './AudioLibraryPanel'
import { TIME_TAG_VALUES, formatSyncUpdatedAt } from './utils'
import { useI18n } from '../../i18n'
import { folderValue, selectedFolderLabel } from '../v2/v2Utils'
import { getApiErrorMessage, uploadLocalMedia } from '../../api'
import { useToast } from '../../contexts/ToastContext'
import PaidSextingFields from '../../components/PaidSextingFields'
import PaidScriptSettingsPanel from '../../components/PaidScriptSettingsPanel'

export default function ModelDetailMediaSection({
  modelId,
  model,
  media,
  mediaGroups,
  loadingMedia,
  mediaError,
  mediaSyncing,
  syncStatusLabel,
  selectedIds,
  moveTargetSessionId,
  setMoveTargetSessionId,
  moving,
  showSessionEditModal,
  setShowSessionEditModal,
  editingSessionName,
  setEditingSessionName,
  editingSessionDescription,
  setEditingSessionDescription,
  editingSessionTags,
  setEditingSessionTags,
  editingSessionTimeTag,
  setEditingSessionTimeTag,
  editingSessionIsScript,
  setEditingSessionIsScript,
  editingSessionPaidSexting,
  setEditingSessionPaidSexting,
  savingSessionName,
  groupToEdit,
  setGroupToEdit,
  handleSaveGroup,
  showCreateSessionInput,
  setShowCreateSessionInput,
  newSessionName,
  setNewSessionName,
  newSessionIsScript,
  setNewSessionIsScript,
  newSessionPaidSexting,
  setNewSessionPaidSexting,
  creatingSession,
  sessions,
  handleSyncMedia,
  handleCancelSyncMedia,
  handleDownloadAllFullMedia,
  handleDownloadOneFullMedia,
  handleDescribePhotos,
  describingPhotos = false,
  toggleMediaSelection,
  clearSelection,
  handleMoveToSession,
  handleCreateSessionAndMove,
  openSessionEdit,
  saveSessionName,
  sessionToDelete,
  setSessionToDelete,
  deletingSession,
  handleDeleteSession,
  groupToDelete,
  setGroupToDelete,
  deletingGroup,
  handleDeleteGroup,
  handleCreateGroup,
  handleReorderGroups,
  handleReorderSessions,
  handleMoveMediaToGroup,
  handleRemoveMediaFromSession,
  updateMediaItem,
  loadMedia,
  handleReorderMedia,
  editModalItem,
  setEditModalItem,
  downloadQueueCount = 0,
  mediaFolders = [],
  mediaFolder = '',
  setMediaFolder,
  refreshMediaFolders,
  mediaLimit = '120',
  setMediaLimit,
  showHiddenMedia = false,
  setShowHiddenMedia,
  mediaTotal = 0,
  mediaHasMore = false,
  highQualityThumbnails = false,
  allowDownloads = true,
  allowAutomationSettings = true,
}) {
  const { t } = useI18n()
  const { addToast } = useToast()

  function handleMediaSaved(updated) {
    updateMediaItem(updated)
    if (updated?._visibility_changed) {
      clearSelection?.()
      loadMedia?.()
      refreshMediaFolders?.()
    }
  }

  const mediaList = media || []
  const downloadingCount = mediaList.filter((m) => m.media_status === 'downloading').length
  const totalInProgress = downloadingCount + downloadQueueCount
  const totalCount = mediaList.length
  const downloadedCount = mediaList.filter((m) => m.media_path).length
  const scopeLabel = selectedFolderLabel(mediaFolders, mediaFolder)
  const effectiveMediaTotal = Number(mediaTotal || 0)
  const loadedSummary = effectiveMediaTotal > totalCount
    ? `${totalCount} of ${effectiveMediaTotal}`
    : String(totalCount)
  const folderOptions = [
    { value: '', label: 'All content sources' },
    ...mediaFolders.map((item) => {
      const countText = typeof item.count === 'number' ? ` (${item.count})` : ''
      const statusText = item.status ? ` - ${item.status}` : ''
      return {
        value: folderValue(item),
        label: `${item.name || item.source || 'Unnamed source'}${countText}${statusText}`,
      }
    }),
  ]
  const limitOptions = [
    { value: '60', label: '60 media' },
    { value: '80', label: '80 media' },
    { value: '120', label: '120 media' },
    { value: '240', label: '240 media' },
    { value: '500', label: '500 media' },
  ]

  const dragFromRef = useRef(null)       // { sessionId, groupId, fromIndex, mediaId }
  const dropTargetRef = useRef(null)     // { sessionId, groupId, dropIndex }
  const groupDragFromRef = useRef(null)  // { sessionId, groupIndex }
  const groupDropTargetRef = useRef(null) // { sessionId, dropIndex } — индекс вставки (0 = до первой группы)
  const sessionDragFromRef = useRef(null)     // { sessionIndex }
  const sessionDropTargetRef = useRef(null)   // { dropIndex }
  const mediaGroupsRef = useRef(mediaGroups)
  const localUploadInputRef = useRef(null)
  mediaGroupsRef.current = mediaGroups

  const [dropTarget, setDropTarget] = useState(null)
  const [sourceHidden, setSourceHidden] = useState(false)
  const [groupDropTarget, setGroupDropTarget] = useState(null)
  const [groupSourceHidden, setGroupSourceHidden] = useState(false)
  const [sessionDropTarget, setSessionDropTarget] = useState(null)
  const [sessionSourceHidden, setSessionSourceHidden] = useState(false)
  const [viewerMediaId, setViewerMediaId] = useState(null)
  const [showDownloadAllConfirm, setShowDownloadAllConfirm] = useState(false)
  const [downloadingAll, setDownloadingAll] = useState(false)
  const [activeContentTab, setActiveContentTab] = useState('photosets')
  const [uploadingLocalMedia, setUploadingLocalMedia] = useState(false)

  const viewerItem = viewerMediaId != null
    ? (media || []).find((m) => m.id === viewerMediaId)
    : null

  const handleViewMedia = useCallback((item) => {
    if (!item?.id) return
    setViewerMediaId(item.id)
    if (allowDownloads && !item.media_path && item.media_status !== 'downloading') {
      handleDownloadOneFullMedia?.(item.id)
    }
  }, [allowDownloads, handleDownloadOneFullMedia])

  const handleConfirmDownloadAll = useCallback(async () => {
    if (!allowDownloads || !handleDownloadAllFullMedia) return
    setDownloadingAll(true)
    try {
      await handleDownloadAllFullMedia()
      setShowDownloadAllConfirm(false)
    } finally {
      setDownloadingAll(false)
    }
  }, [allowDownloads, handleDownloadAllFullMedia])

  const handleAudioUploaded = useCallback((created) => {
    if (!created) return
    loadMedia?.()
  }, [loadMedia])

  const handleLocalMediaFiles = useCallback(async (event) => {
    const files = Array.from(event.target.files || [])
    event.target.value = ''
    if (!files.length || !modelId) return
    setUploadingLocalMedia(true)
    try {
      const result = await uploadLocalMedia(modelId, files)
      const created = Number(result?.created || result?.data?.length || files.length)
      addToast(`Uploaded ${created} media file${created === 1 ? '' : 's'}`, 'success')
      setMediaFolder?.('source:local_upload')
      refreshMediaFolders?.()
      setTimeout(() => loadMedia?.(), 200)
    } catch (err) {
      addToast(getApiErrorMessage(err, 'Failed to upload media'), 'error')
    } finally {
      setUploadingLocalMedia(false)
    }
  }, [addToast, loadMedia, modelId, refreshMediaFolders, setMediaFolder])

  const handleItemDragStart = useCallback((sessionId, groupId, fromIndex, mediaId) => {
    dragFromRef.current = { sessionId, groupId, fromIndex, mediaId }
    setTimeout(() => {
      const next = { sessionId, groupId, dropIndex: fromIndex }
      dropTargetRef.current = next
      setDropTarget(next)
      setSourceHidden(true)
    }, 0)
  }, [])

  const handleItemDragOver = useCallback((e, sessionId, groupId, itemIndex) => {
    const from = dragFromRef.current
    if (!from || from.sessionId !== sessionId) return
    const rect = e.currentTarget.getBoundingClientRect()
    const isAfter = e.clientX > rect.left + rect.width / 2
    const dropIndex = isAfter ? itemIndex + 1 : itemIndex
    const prev = dropTargetRef.current
    if (prev?.dropIndex === dropIndex && prev?.sessionId === sessionId && prev?.groupId === groupId) return
    const next = { sessionId, groupId, dropIndex }
    dropTargetRef.current = next
    setDropTarget(next)
  }, [])

  const doReorder = useCallback((sessionId, groupId, fromIndex, dropIndex, onApplied) => {
    if (!handleReorderMedia) return
    if (dropIndex === null || dropIndex === fromIndex || dropIndex === fromIndex + 1) return
    const grp = mediaGroupsRef.current.find((g) => g.session?.id === sessionId)
    if (!grp) return
    const targetMediaGroup = grp.mediaGroups?.find((mg) => (mg.id === groupId || (mg.id == null && groupId == null)))
    const list = targetMediaGroup ? [...targetMediaGroup.media] : []
    if (fromIndex >= list.length) return
    const [removed] = list.splice(fromIndex, 1)
    const adjustedIndex = dropIndex > fromIndex ? dropIndex - 1 : dropIndex
    list.splice(adjustedIndex, 0, removed)
    handleReorderMedia(sessionId, list.map((m) => m.id), groupId, onApplied)
  }, [handleReorderMedia])

  const handleItemDrop = useCallback((sessionId, groupId) => {
    const from = dragFromRef.current
    const dropIndex = dropTargetRef.current?.dropIndex ?? null
    dragFromRef.current = null
    dropTargetRef.current = null
    setDropTarget(null)
    if (!from || from.sessionId !== sessionId) return
    if (from.groupId === groupId) {
      doReorder(sessionId, groupId, from.fromIndex, dropIndex, () => setSourceHidden(false))
    } else {
      handleMoveMediaToGroup?.(sessionId, groupId, [from.mediaId], dropIndex, () => setSourceHidden(false))
    }
  }, [doReorder, handleMoveMediaToGroup])

  const handleGroupDragStart = useCallback((sessionId, groupIndex) => {
    groupDragFromRef.current = { sessionId, groupIndex }
    setTimeout(() => {
      groupDropTargetRef.current = { sessionId, dropIndex: groupIndex }
      setGroupDropTarget({ sessionId, dropIndex: groupIndex })
      setGroupSourceHidden(true)
    }, 0)
  }, [])

  const handleGroupDragOver = useCallback((e, sessionId, groupIndex) => {
    const from = groupDragFromRef.current
    if (!from || from.sessionId !== sessionId) return
    e.preventDefault()
    const rect = e.currentTarget.getBoundingClientRect()
    const isAfter = e.clientY > rect.top + rect.height / 2
    const dropIndex = isAfter ? groupIndex + 1 : groupIndex
    const prev = groupDropTargetRef.current
    if (prev?.sessionId === sessionId && prev?.dropIndex === dropIndex) return
    groupDropTargetRef.current = { sessionId, dropIndex }
    setGroupDropTarget({ sessionId, dropIndex })
  }, [])

  const handleGroupPlaceholderDragOver = useCallback((e, sessionId, dropIndex) => {
    const from = groupDragFromRef.current
    if (!from || from.sessionId !== sessionId) return
    e.preventDefault()
    e.stopPropagation()
    const prev = groupDropTargetRef.current
    if (prev?.sessionId === sessionId && prev?.dropIndex === dropIndex) return
    groupDropTargetRef.current = { sessionId, dropIndex }
    setGroupDropTarget({ sessionId, dropIndex })
  }, [])

  const handleGroupDrop = useCallback(async (sessionId, dropIndex, from) => {
    if (!from || from.sessionId !== sessionId) return
    const grp = mediaGroupsRef.current.find((g) => g.session?.id === sessionId)
    if (!grp?.mediaGroups) return
    const reorderable = grp.mediaGroups.filter((mg) => mg.id != null)
    if (from.groupIndex === dropIndex || from.groupIndex === dropIndex - 1) return
    const [removed] = reorderable.splice(from.groupIndex, 1)
    const adj = dropIndex > from.groupIndex ? dropIndex - 1 : dropIndex
    reorderable.splice(adj, 0, removed)
    await handleReorderGroups?.(sessionId, reorderable.map((g) => g.id))
  }, [handleReorderGroups])

  const doGroupDrop = useCallback(async () => {
    const from = groupDragFromRef.current
    const dt = groupDropTargetRef.current
    if (!from || !dt) return
    groupDragFromRef.current = null
    groupDropTargetRef.current = null
    setGroupDropTarget(null)
    try {
      if (dt.sessionId === from.sessionId && dt.dropIndex != null) {
        await handleGroupDrop(from.sessionId, dt.dropIndex, from)
      }
    } finally {
      setGroupSourceHidden(false)
    }
  }, [handleGroupDrop])

  const reorderableSessions = (mediaGroups || []).filter((g) => g.session?.id != null)
  const sessionReorderableIds = reorderableSessions.map((g) => g.session.id)

  const handleSessionDragStart = useCallback((sessionIndex) => {
    sessionDragFromRef.current = { sessionIndex }
    setTimeout(() => {
      sessionDropTargetRef.current = { dropIndex: sessionIndex }
      setSessionDropTarget({ dropIndex: sessionIndex })
      setSessionSourceHidden(true)
    }, 0)
  }, [])

  const handleSessionDragOver = useCallback((e, sessionIndex) => {
    const from = sessionDragFromRef.current
    if (!from) return
    e.preventDefault()
    const rect = e.currentTarget.getBoundingClientRect()
    const isAfter = e.clientY > rect.top + rect.height / 2
    const dropIndex = isAfter ? sessionIndex + 1 : sessionIndex
    const prev = sessionDropTargetRef.current
    if (prev?.dropIndex === dropIndex) return
    sessionDropTargetRef.current = { dropIndex }
    setSessionDropTarget({ dropIndex })
  }, [])

  const doSessionDrop = useCallback(async () => {
    const from = sessionDragFromRef.current
    const dt = sessionDropTargetRef.current
    sessionDragFromRef.current = null
    sessionDropTargetRef.current = null
    setSessionDropTarget(null)
    if (!from || !dt || dt.dropIndex == null) {
      setSessionSourceHidden(false)
      return
    }
    if (from.sessionIndex === dt.dropIndex || from.sessionIndex === dt.dropIndex - 1) {
      setSessionSourceHidden(false)
      return
    }
    try {
      const next = [...sessionReorderableIds]
      const [removed] = next.splice(from.sessionIndex, 1)
      const adj = dt.dropIndex > from.sessionIndex ? dt.dropIndex - 1 : dt.dropIndex
      next.splice(adj, 0, removed)
      await handleReorderSessions?.(next)
    } finally {
      setSessionSourceHidden(false)
    }
  }, [handleReorderSessions, sessionReorderableIds])

  const handleSessionDragEnd = useCallback(() => {
    const from = sessionDragFromRef.current
    const dt = sessionDropTargetRef.current
    if (from && dt) doSessionDrop()
    else {
      sessionDragFromRef.current = null
      sessionDropTargetRef.current = null
      setSessionDropTarget(null)
      setSessionSourceHidden(false)
    }
  }, [doSessionDrop])

  const handleGroupDragEnd = useCallback(() => {
    const from = groupDragFromRef.current
    const dt = groupDropTargetRef.current
    if (from && dt) doGroupDrop()
    else {
      groupDragFromRef.current = null
      groupDropTargetRef.current = null
      setGroupDropTarget(null)
      setGroupSourceHidden(false)
    }
  }, [doGroupDrop])

  const handleDragEnd = useCallback(() => {
    const from = dragFromRef.current
    const dt = dropTargetRef.current
    dragFromRef.current = null
    dropTargetRef.current = null
    setDropTarget(null)
    if (from && dt && dt.sessionId === from.sessionId) {
      if (dt.groupId === from.groupId) {
        doReorder(from.sessionId, from.groupId, from.fromIndex, dt.dropIndex, () => setSourceHidden(false))
      } else {
        handleMoveMediaToGroup?.(from.sessionId, dt.groupId, [from.mediaId], dt.dropIndex, () => setSourceHidden(false))
      }
    } else {
      requestAnimationFrame(() => setSourceHidden(false))
    }
  }, [doReorder, handleMoveMediaToGroup])

  const timeTagOptions = TIME_TAG_VALUES.map((value) => ({
    value,
    label:
      value === ''
        ? t('media.session.edit.timeOfDayPlaceholder')
        : t(`media.timeOfDay.${value}`),
  }))
  const groupPriceLabel = (group) => {
    if (typeof group?.price_min !== 'number' || typeof group?.price_max !== 'number') return null
    return group.price_min === group.price_max
      ? `$${group.price_max}`
      : `$${group.price_min}-${group.price_max}`
  }

  return (
    <>
      {allowAutomationSettings ? <PaidScriptSettingsPanel key={`paid-settings:${modelId}`} modelId={modelId} /> : null}
      <div className="model-detail-media-actions">
        <div className="model-detail-media-actions-left">
          <h2 className="model-detail-media-section-title">
            {activeContentTab === 'audio' ? t('media.section.audio') : t('media.section.photosets')}
          </h2>
          <div className="model-detail-media-tabs" role="tablist" aria-label={t('media.section.media')}>
            <button
              type="button"
              className={`model-detail-media-tab${activeContentTab === 'photosets' ? ' active' : ''}`}
              onClick={() => setActiveContentTab('photosets')}
              role="tab"
              aria-selected={activeContentTab === 'photosets'}
            >
              {t('media.section.photosets')}
            </button>
            <button
              type="button"
              className={`model-detail-media-tab${activeContentTab === 'audio' ? ' active' : ''}`}
              onClick={() => setActiveContentTab('audio')}
              role="tab"
              aria-selected={activeContentTab === 'audio'}
            >
              {t('media.section.audio')}
            </button>
          </div>
          {totalCount > 0 && (
            <span className="model-detail-media-stats">
              {t('media.stats.downloadedOfTotal', { downloaded: downloadedCount, total: totalCount })}
              {downloadQueueCount > 0 || downloadingCount > 0 ? (
                <> · {t('media.stats.inQueue', { count: totalInProgress })}</>
              ) : null}
            </span>
          )}
        </div>
        <div className="model-detail-media-actions-right">
          {syncStatusLabel && (
            <span className="model-detail-media-sync-info">
              {syncStatusLabel}
              {model?.media_sync_status === 'failed' && model?.media_sync_error && (
                <span className="model-detail-media-sync-error" title={model.media_sync_error}>
                  {' · '}
                  {model.media_sync_error}
                </span>
              )}
              {formatSyncUpdatedAt(model?.media_sync_updated_at) && (
                <span className="model-detail-media-sync-date">
                  {' · '}
                  {formatSyncUpdatedAt(model.media_sync_updated_at)}
                </span>
              )}
            </span>
          )}
          {mediaSyncing ? (
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={handleCancelSyncMedia}
              title={t('media.sync.stop.title')}
              aria-label={t('media.sync.stop.title')}
            >
              <svg viewBox="0 0 24 24" className="model-detail-btn-icon model-detail-btn-icon--spinning" aria-hidden>
                <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M21 3v5h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {t('media.sync.stop')}
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => handleSyncMedia(false)}
              disabled={loadingMedia}
              title={t('media.sync.media')}
              aria-label={t('media.sync.media')}
            >
              <svg viewBox="0 0 24 24" className="model-detail-btn-icon" aria-hidden>
                <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M21 3v5h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {t('media.sync.media')}
            </button>
          )}
          {allowDownloads ? <Tooltip text={t('media.downloadAll.confirmMessage')}>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setShowDownloadAllConfirm(true)}
              disabled={!modelId || loadingMedia || mediaSyncing}
              title={t('media.downloadAll.title')}
              aria-label={t('media.downloadAll.title')}
            >
              <svg viewBox="0 0 24 24" className="model-detail-btn-icon" aria-hidden>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <polyline points="7 10 12 15 17 10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <line x1="12" y1="15" x2="12" y2="3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {t('media.downloadAll')}
            </button>
          </Tooltip> : null}
        </div>
      </div>

      {activeContentTab !== 'audio' && (
        <div className="model-detail-media-scope-controls">
          <label className="model-detail-media-scope-field">
            <span>Source / folder</span>
            <Select
              value={mediaFolder}
              options={folderOptions}
              onChange={(value) => setMediaFolder?.(value)}
              dropdownPosition="down"
              dropdownAutoWidth
            />
          </label>
          <label className="model-detail-media-scope-field model-detail-media-scope-field--limit">
            <span>Load limit</span>
            <Select
              value={mediaLimit}
              options={limitOptions}
              onChange={(value) => setMediaLimit?.(value)}
              dropdownPosition="down"
            />
          </label>
          <div className="model-detail-media-scope-field">
            <span>Visibility</span>
            <Switch
              checked={showHiddenMedia}
              onChange={(checked) => setShowHiddenMedia?.(checked)}
              label="Show hidden media"
              ariaLabel="Show hidden media"
            />
          </div>
          <div className="model-detail-media-scope-summary">
            <strong>{scopeLabel}</strong>
            <span>
              {loadingMedia ? 'Loading media...' : `${loadedSummary} loaded`}
              {mediaHasMore ? ' in current page' : ''}
            </span>
          </div>
          <input
            ref={localUploadInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,video/mp4,video/webm,video/quicktime,.jpg,.jpeg,.jfif,.png,.webp,.gif,.heic,.heif,.mp4,.webm,.mov,.m4v"
            multiple
            onChange={handleLocalMediaFiles}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => localUploadInputRef.current?.click()}
            disabled={!modelId || uploadingLocalMedia}
            title="Upload photos or videos, including HEIC/HEIF (max 50 MB); HEIC originals are retained with JPEG copies for viewing and delivery"
            aria-label="Upload photos or videos from this computer"
          >
            <svg viewBox="0 0 24 24" className="model-detail-btn-icon" aria-hidden>
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <polyline points="17 8 12 3 7 8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <line x1="12" y1="3" x2="12" y2="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {uploadingLocalMedia ? 'Uploading...' : 'Upload media'}
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => refreshMediaFolders?.()}
            disabled={!modelId || loadingMedia}
          >
            Refresh sources
          </button>
          <Tooltip text="Generate missing photo descriptions for the selected source with MiniMax M3.">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => handleDescribePhotos?.()}
              disabled={!modelId || loadingMedia || describingPhotos}
            >
              {describingPhotos ? 'Queueing descriptions...' : 'Describe photos'}
            </button>
          </Tooltip>
        </div>
      )}

      {activeContentTab !== 'audio' && (
        <div className="model-detail-media-price-guide">
          <div>
            <strong>{t('media.group.price.guideTitle', 'Price and description levels')}</strong>
            <span>{t('media.group.price.guideText', 'Use media edit for a single item description or individual media price. Use group edit for the whole step description and stage price.')}</span>
          </div>
        </div>
      )}

      {activeContentTab === 'audio' ? (
        <AudioLibraryPanel
          modelId={modelId}
          mediaList={mediaList}
          onView={handleViewMedia}
          onUploaded={handleAudioUploaded}
        />
      ) : loadingMedia && !media?.length ? (
        <p className="model-detail-loading">{t('media.sync.loading')}</p>
      ) : media?.length ? (
        <div className="model-detail-media-groups">
          {mediaGroups.map((grp) => {
            const sessionIndex = grp.session?.id != null
              ? sessionReorderableIds.indexOf(grp.session.id)
              : -1
            const canDragSession = sessionIndex >= 0
            const isDraggingThisSession =
              canDragSession &&
              sessionDragFromRef.current?.sessionIndex === sessionIndex
            const showSessionPhBefore =
              canDragSession &&
              sessionDropTarget?.dropIndex === sessionIndex
            const showSessionPhAfter =
              canDragSession &&
              sessionIndex === reorderableSessions.length - 1 &&
              sessionDropTarget?.dropIndex === reorderableSessions.length
            const sessionPh = (key) => (
              <div
                key={key}
                className="session-drop-placeholder"
                aria-hidden
                onDragOver={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                }}
                onDrop={(e) => {
                  e.preventDefault()
                  doSessionDrop()
                }}
              />
            )
            return (
            <React.Fragment key={grp.key}>
            {showSessionPhBefore && sessionPh(`ses-ph-before-${grp.key}`)}
            <div
              id={`media-group-${grp.key}`}
              className={`model-detail-media-group${
                sessionSourceHidden && isDraggingThisSession
                  ? ' model-detail-media-group--source-hidden'
                  : ''
              }`}
              onDragOver={canDragSession ? (e) => handleSessionDragOver(e, sessionIndex) : undefined}
              onDrop={canDragSession ? (e) => { e.preventDefault(); doSessionDrop() } : undefined}
            >
              <div className="model-detail-media-group-header">
                <div className="model-detail-media-group-header-left">
                  {canDragSession && (
                    <span
                      className="model-detail-media-session-drag-handle"
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.effectAllowed = 'move'
                        e.dataTransfer.setData('text/plain', 'session')
                        handleSessionDragStart(sessionIndex)
                      }}
                      onDragEnd={handleSessionDragEnd}
                      title={t('media.session.reorder', 'Перетащить сессию')}
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" opacity={0.5}>
                        <circle cx="9" cy="6" r="1.5" />
                        <circle cx="15" cy="6" r="1.5" />
                        <circle cx="9" cy="12" r="1.5" />
                        <circle cx="15" cy="12" r="1.5" />
                        <circle cx="9" cy="18" r="1.5" />
                        <circle cx="15" cy="18" r="1.5" />
                      </svg>
                    </span>
                  )}
                  <h3 className="model-detail-media-group-title">
                    {grp.name ?? t('media.section.unmarked', 'Неразмеченные')}
                    {grp.session && (
                      <>
                        <span className="model-detail-media-group-title-gap" />
                        <span className="model-detail-media-group-title-badges">
                          {grp.session?.is_script && (
                            <Tooltip text={t('media.session.scriptTooltip')}>
                              <span className="model-detail-session-script-label">
                                {t('media.session.scriptLabel')}
                              </span>
                            </Tooltip>
                          )}
                          <AccountUsageIndicator
                            isReady={grp.session.is_active === true}
                            title={
                              grp.session.is_active
                                ? t('media.session.active.ok')
                                : t('media.session.active.bad')
                            }
                            className="model-detail-session-usage-indicator"
                          />
                        </span>
                        {grp.session?.time_tag && (
                          <span className="model-detail-media-group-time-tag">
                            {timeTagOptions.find((o) => o.value === grp.session.time_tag)?.label ??
                              grp.session.time_tag}
                          </span>
                        )}
                      </>
                    )}
                  </h3>
                  {grp.session?.description ? (
                    <p className="model-detail-session-description">{grp.session.description}</p>
                  ) : null}
                  {Array.isArray(grp.session?.selection_tags) && grp.session.selection_tags.length ? (
                    <div className="model-detail-session-tags">
                      {grp.session.selection_tags.slice(0, 8).map((tag) => (
                        <span key={tag}>{tag}</span>
                      ))}
                    </div>
                  ) : null}
                </div>
                {grp.session && (
                  <span className="model-detail-media-group-actions">
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => openSessionEdit(grp.session)}
                      title={t('media.session.edit.title')}
                      aria-label={t('media.session.edit.title')}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => setSessionToDelete(grp.session)}
                      title={t('media.session.delete.title')}
                      aria-label={t('media.session.delete.title')}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        <line x1="10" y1="11" x2="10" y2="17" />
                        <line x1="14" y1="11" x2="14" y2="17" />
                      </svg>
                    </button>
                  </span>
                )}
              </div>
              <div className="model-detail-media-groups-inner">
                {(grp.mediaGroups || [{ id: null, name: null, media: grp.media || [] }])
                  .filter((mg) => mg.id != null || (mg.media?.length ?? 0) > 0)
                  .map((mediaGroup) => {
                  const groupId = mediaGroup.id
                  const groupMedia = mediaGroup.media || []
                  const isScript = grp.session?.is_script
                  const canReorder = !!grp.session
                  const sid = grp.session?.id

                  if (!grp.session) {
                    return (
                      <div key="ungrouped" className="model-detail-media-subgroup">
                        <div
                          className={`model-detail-media-grid${isScript ? ' model-detail-media-grid--script' : ''}`}
                        >
                          {groupMedia.map((item, i) => (
                            <ModelDetailMediaItem
                              key={`item-${item.id ?? i}`}
                              item={item}
                              index={i}
                              modelId={modelId}
                              isSelected={selectedIds.has(item.id)}
                              onToggle={toggleMediaSelection}
                              onView={handleViewMedia}
                              onEdit={setEditModalItem}
                              onLibrarySaved={handleMediaSaved}
                              canReorder={false}
                              highQualityThumbnail={false}
                            />
                          ))}
                        </div>
                      </div>
                    )
                  }

                  const from = dragFromRef.current
                  const isActive = !!from && from.sessionId === sid
                  const dt = dropTarget
                  const isDropActive = isActive && !!dt && dt.sessionId === sid && dt.groupId === groupId && dt.dropIndex != null
                  const dropIdx = isDropActive ? dt.dropIndex : -1
                  const isMediaDragToOtherGroup = isActive && from?.groupId !== groupId

                  const placeholderNode = (
                    <div
                      key="__drop__"
                      className="media-drop-placeholder"
                      aria-hidden
                      onDragOver={(e) => { e.preventDefault(); e.stopPropagation() }}
                      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleItemDrop(sid, groupId) }}
                    />
                  )

                  const reorderableGroups = (grp.mediaGroups || []).filter((mg) => mg.id != null)
                  const groupIndexInReorderable = groupId != null ? reorderableGroups.findIndex((g) => g.id === groupId) : -1
                  const canDragGroup = groupId != null && groupIndexInReorderable >= 0
                  const groupFrom = groupDragFromRef.current
                  const isDraggingThisGroup = groupFrom?.sessionId === sid && groupFrom?.groupIndex === groupIndexInReorderable
                  const showPlaceholderBefore = canDragGroup && groupDropTarget?.sessionId === sid && groupDropTarget?.dropIndex === groupIndexInReorderable
                  const showPlaceholderAfter = canDragGroup && groupIndexInReorderable === reorderableGroups.length - 1 && groupDropTarget?.sessionId === sid && groupDropTarget?.dropIndex === reorderableGroups.length

                  const groupDropPlaceholder = (dropIdx) => (
                    <div
                      key={`group-ph-${dropIdx}`}
                      className="group-drop-placeholder"
                      aria-hidden
                      onDragOver={(e) => handleGroupPlaceholderDragOver(e, sid, dropIdx)}
                      onDrop={(e) => { e.preventDefault(); doGroupDrop() }}
                    />
                  )

                  return (
                    <React.Fragment key={groupId ?? 'ungrouped'}>
                      {showPlaceholderBefore && groupDropPlaceholder(groupIndexInReorderable)}
                      <div
                        className={`model-detail-media-subgroup${groupSourceHidden && isDraggingThisGroup ? ' model-detail-media-subgroup--source-hidden' : ''}`}
                        onDragOver={canDragGroup ? (e) => handleGroupDragOver(e, sid, groupIndexInReorderable) : undefined}
                        onDrop={canDragGroup ? (e) => { e.preventDefault(); doGroupDrop() } : undefined}
                      >
                      {(groupId != null || groupMedia.length > 0) && (
                        <div className="model-detail-media-subgroup-header">
                          {groupId != null ? (
                            <>
                              {canDragGroup && (
                                <span
                                  className="model-detail-media-subgroup-drag-handle"
                                  draggable
                                  onDragStart={(e) => {
                                    e.dataTransfer.effectAllowed = 'move'
                                    e.dataTransfer.setData('text/plain', 'group')
                                    handleGroupDragStart(sid, groupIndexInReorderable)
                                  }}
                                  onDragEnd={handleGroupDragEnd}
                                  title={t('media.group.reorder', 'Перетащить группу')}
                                >
                                  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" opacity={0.5}>
                                    <circle cx="9" cy="6" r="1.5" />
                                    <circle cx="15" cy="6" r="1.5" />
                                    <circle cx="9" cy="12" r="1.5" />
                                    <circle cx="15" cy="12" r="1.5" />
                                    <circle cx="9" cy="18" r="1.5" />
                                    <circle cx="15" cy="18" r="1.5" />
                                  </svg>
                                </span>
                              )}
                              <span className="model-detail-media-subgroup-name">
                                {mediaGroup.name || t('media.group.unnamed', 'Без названия')}
                                <MediaComposition items={groupMedia} />
                                {typeof mediaGroup.price_min === 'number' && typeof mediaGroup.price_max === 'number' && (
                                  <span className="model-detail-media-group-price-badge">
                                    {groupPriceLabel(mediaGroup)}
                                  </span>
                                )}
                                <AccountUsageIndicator
                                  isReady={mediaGroup.is_ready_for_sale === true}
                                  title={
                                    mediaGroup.is_ready_for_sale
                                      ? t('media.group.readyForSale', 'Группа готова к продаже')
                                      : (mediaGroup.missing_for_sale?.length
                                          ? t('media.group.missingForSale', 'Заполните цену группы')
                                          : t('media.group.notReadyForSale', 'Группа не готова к продаже'))
                                  }
                                  className="model-detail-group-usage-indicator"
                                />
                              </span>
                              <span className="model-detail-media-subgroup-actions">
                                <button
                                  type="button"
                                  className="btn btn-secondary btn-sm model-detail-media-price-button"
                                  onClick={() => setGroupToEdit?.({ group: mediaGroup, session: grp.session })}
                                  title={t('media.group.price.edit', 'Set step price and description')}
                                  aria-label={t('media.group.price.edit', 'Set step price and description')}
                                >
                                  {groupPriceLabel(mediaGroup) ? t('media.group.price.editShort', 'Edit step') : t('media.group.price.setShort', 'Set price')}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-ghost btn-sm"
                                  onClick={() => setGroupToEdit?.({ group: mediaGroup, session: grp.session })}
                                  title={t('media.group.edit.title')}
                                  aria-label={t('media.group.edit.title')}
                                >
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-danger btn-sm"
                                  onClick={() => setGroupToDelete({ group: mediaGroup, session: grp.session })}
                                  title={t('media.session.delete.title')}
                                  aria-label={t('media.session.delete.title')}
                                >
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                                    <polyline points="3 6 5 6 21 6" />
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                    <line x1="10" y1="11" x2="10" y2="17" />
                                    <line x1="14" y1="11" x2="14" y2="17" />
                                  </svg>
                                </button>
                              </span>
                            </>
                          ) : (
                            <span className="model-detail-media-subgroup-name model-detail-media-subgroup-ungrouped">
                              {t('media.group.ungrouped', 'Без группы')}
                            </span>
                          )}
                        </div>
                      )}
                      <div
                        className={`model-detail-media-grid${isScript ? ' model-detail-media-grid--script' : ''}`}
                        onDragOver={canReorder ? (e) => {
                          e.preventDefault()
                          const f = dragFromRef.current
                          if (!f || f.sessionId !== sid) return
                          // Внутри одной группы позицию задаёт onDragOver на каждом item — не перезаписываем
                          if (f.groupId === groupId) return
                          const idx = groupMedia.length
                          const prev = dropTargetRef.current
                          if (prev?.groupId !== groupId || prev?.dropIndex !== idx) {
                            dropTargetRef.current = { sessionId: sid, groupId, dropIndex: idx }
                            setDropTarget({ sessionId: sid, groupId, dropIndex: idx })
                          }
                        } : undefined}
                        onDrop={canReorder ? (e) => { e.preventDefault(); handleItemDrop(sid, groupId) } : undefined}
                      >
                        {(() => {
                          const nodes = []
                          if (isDropActive && dropIdx === 0 && groupMedia.length === 0) {
                            nodes.push(<React.Fragment key="ph-empty">{placeholderNode}</React.Fragment>)
                          }
                          groupMedia.forEach((item, i) => {
                            if (isDropActive && dropIdx === i) nodes.push(<React.Fragment key={`ph-${i}`}>{placeholderNode}</React.Fragment>)
                            nodes.push(
                              <ModelDetailMediaItem
                                key={`item-${item.id ?? i}`}
                                item={item}
                                sessionId={sid}
                                index={i}
                                modelId={modelId}
                                isSelected={selectedIds.has(item.id)}
                                onToggle={toggleMediaSelection}
                                onView={handleViewMedia}
                                onEdit={setEditModalItem}
                                onLibrarySaved={handleMediaSaved}
                                canReorder={canReorder}
                                isDragging={isActive && from?.groupId === groupId && from?.fromIndex === i}
                                isSourceHidden={sourceHidden && isActive && from?.groupId === groupId && from?.fromIndex === i}
                                highQualityThumbnail={highQualityThumbnails}
                                onRemoveFromSession={handleRemoveMediaFromSession}
                                onDragStart={() => handleItemDragStart(sid, groupId, i, item.id)}
                                onDragOver={(e) => handleItemDragOver(e, sid, groupId, i)}
                                onDrop={() => handleItemDrop(sid, groupId)}
                                onDragEnd={handleDragEnd}
                              />
                            )
                            if (i === groupMedia.length - 1 && isDropActive && dropIdx === groupMedia.length) {
                              nodes.push(<React.Fragment key="ph-end">{placeholderNode}</React.Fragment>)
                            }
                          })
                          return nodes
                        })()}
                      </div>
                    </div>
                      {showPlaceholderAfter && groupDropPlaceholder(reorderableGroups.length)}
                    </React.Fragment>
                    )
                })}
                {grp.session && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm model-detail-media-group-add"
                    onClick={() => handleCreateGroup(grp.session.id)}
                    title={t('media.group.add', 'Добавить группу')}
                    aria-label={t('media.group.add', 'Добавить группу')}
                  >
                    + {t('media.group.addShort', 'Группа')}
                  </button>
                )}
              </div>
            </div>
            {showSessionPhAfter && sessionPh(`ses-ph-after-${grp.key}`)}
            </React.Fragment>
            )
          })}
        </div>
      ) : (
        <p className="model-detail-empty">{mediaError || t('media.none')}</p>
      )}

      {selectedIds.size > 0 && (
        <div className="model-detail-move-bar">
          <div className="model-detail-move-bar-inner">
            <span className="model-detail-move-bar-count">
              {t('media.selectedCount', { count: selectedIds.size })}
            </span>
            <div className="model-detail-move-bar-controls">
              {showCreateSessionInput ? (
                <div className="model-detail-move-create-session">
                  <input
                    type="text"
                    className="model-detail-move-create-input"
                    placeholder={t('media.session.name.placeholder')}
                    value={newSessionName}
                    onChange={(e) => setNewSessionName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleCreateSessionAndMove()
                      if (e.key === 'Escape') setShowCreateSessionInput(false)
                    }}
                    autoFocus
                  />
                  <div className="model-detail-move-create-script">
                    <Switch
                      checked={newSessionIsScript}
                      onChange={setNewSessionIsScript}
                      label={t('media.session.edit.isScriptLabel')}
                      ariaLabel={t('media.session.edit.isScriptLabel')}
                    />
                  </div>
                  {newSessionIsScript ? <PaidSextingFields value={newSessionPaidSexting} onChange={setNewSessionPaidSexting} disabled={creatingSession} /> : null}
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleCreateSessionAndMove}
                    disabled={creatingSession || !newSessionName.trim()}
                  >
                    {creatingSession
                      ? t('media.session.creating')
                      : t('media.session.createAndMove')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      setShowCreateSessionInput(false)
                      setMoveTargetSessionId(null)
                    }}
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              ) : (
                <>
                  <Select
                    className="model-detail-move-select"
                    value={moveTargetSessionId ?? ''}
                    placeholder={t('media.session.noneOption')}
                    options={[
                      { value: '', label: t('media.session.noneOption') },
                      ...sessions.map((s) => ({
                        value: String(s.id),
                        label: s.is_script ? `${t('media.session.scriptLabel')}: ${s.name}` : s.name,
                      })),
                      { value: '__create__', label: t('media.session.createOption') },
                    ]}
                    onChange={(v) => {
                      if (v === '__create__') {
                        setShowCreateSessionInput(true)
                        setMoveTargetSessionId('__create__')
                      } else {
                        setMoveTargetSessionId(v === '' ? null : v)
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleMoveToSession}
                    disabled={moving || moveTargetSessionId === '__create__'}
                  >
                    {moving ? t('media.moving') : t('media.move')}
                  </button>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={clearSelection}>
                    {t('media.selection.clear')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <MediaEditModal
        open={!!editModalItem}
        modelId={modelId}
        item={editModalItem}
        onClose={() => setEditModalItem(null)}
        onSaved={handleMediaSaved}
      />

      <SessionEditModal
        session={showSessionEditModal}
        open={!!showSessionEditModal}
        saving={savingSessionName}
        editingName={editingSessionName}
        editingDescription={editingSessionDescription}
        editingTags={editingSessionTags}
        editingTimeTag={editingSessionTimeTag}
        editingIsScript={editingSessionIsScript}
        editingPaidSexting={editingSessionPaidSexting}
        onNameChange={setEditingSessionName}
        onDescriptionChange={setEditingSessionDescription}
        onTagsChange={setEditingSessionTags}
        onTimeTagChange={setEditingSessionTimeTag}
        onIsScriptChange={setEditingSessionIsScript}
        onPaidSextingChange={setEditingSessionPaidSexting}
        onSave={saveSessionName}
        onClose={() => setShowSessionEditModal(null)}
      />

      <GroupEditModal
        open={!!groupToEdit?.group?.id}
        modelId={modelId}
        sessionId={groupToEdit?.session?.id}
        group={groupToEdit?.group}
        onSaved={handleSaveGroup}
        onClose={() => setGroupToEdit(null)}
      />

      <ConfirmModal
        open={!!sessionToDelete}
        title={t('media.session.delete.title')}
        message={t('media.session.delete.message', { name: sessionToDelete?.name ?? '' })}
        confirmLabel={deletingSession ? t('common.deleting') : t('common.delete')}
        cancelLabel={t('common.cancel')}
        danger
        confirmDisabled={deletingSession}
        onConfirm={handleDeleteSession}
        onCancel={() => !deletingSession && setSessionToDelete(null)}
      />

      <ConfirmModal
        open={!!groupToDelete}
        title={t('media.group.delete.title', 'Удалить группу?')}
        message={t('media.group.delete.message', {
          name: groupToDelete?.group?.name || t('media.group.unnamed', 'Без названия'),
        })}
        confirmLabel={deletingGroup ? t('common.deleting') : t('common.delete')}
        cancelLabel={t('common.cancel')}
        danger
        confirmDisabled={deletingGroup}
        onConfirm={handleDeleteGroup}
        onCancel={() => !deletingGroup && setGroupToDelete(null)}
      />

      <ConfirmModal
        open={showDownloadAllConfirm}
        title={t('media.downloadAll.confirmTitle')}
        message={t('media.downloadAll.confirmMessage')}
        confirmLabel={downloadingAll ? t('common.loading') : t('media.downloadAll')}
        cancelLabel={t('common.cancel')}
        confirmDisabled={downloadingAll}
        onConfirm={handleConfirmDownloadAll}
        onCancel={() => !downloadingAll && setShowDownloadAllConfirm(false)}
      />

      <MediaViewer
        open={viewerMediaId != null}
        onClose={() => setViewerMediaId(null)}
        modelId={modelId}
        item={viewerItem}
      />
    </>
  )
}
